import React from 'react';

import { observer } from 'mobx-react';

import { Form, Formik, FormikHelpers } from 'formik';

import { Button } from 'components/Button';

import NavigationBlocker from 'components/NavigationBlocker';

import { formErrors, getErrorMessage } from 'utils/api/errors';

import { useStore } from 'utils/hooks/store';

import { isDynamicGroup } from 'containers/MemberGroups/utils/membership';

import MemberGroupForm, {
  emptyRule,
  MemberGroupFormValues,
  memberGroupValidationSchema,
} from '../components/MemberGroupForm';
import { draftFromRule, RuleDraft } from '../components/RuleBuilder';
import { EditActions, Section, SectionBody, SectionHeader, SectionTitle } from './styles';

const EditMemberGroupDetails: React.FunctionComponent<{ setEditing: (value: boolean) => void }> = observer(
  ({ setEditing }) => {
    const { memberGroupDetails, notification } = useStore();
    const { group, activeRule } = memberGroupDetails;

    if (!group) {
      return null;
    }

    const isDynamic = isDynamicGroup(group);
    const initialValues: MemberGroupFormValues = {
      name: group.name,
      description: group.description ?? '',
      membershipMode: group.membershipMode,
      members: [],
      rule: isDynamic && activeRule ? draftFromRule(activeRule) : emptyRule(),
    };

    const onSubmit = async (
      values: MemberGroupFormValues,
      helpers: FormikHelpers<MemberGroupFormValues>
    ): Promise<void> => {
      const changes: { name?: string; description?: string; rule?: RuleDraft } = {};
      if (values.name.trim() !== group.name) {
        changes.name = values.name.trim();
      }
      if (values.description.trim() !== (group.description ?? '')) {
        changes.description = values.description.trim();
      }
      if (isDynamic) {
        changes.rule = values.rule;
      }

      if (Object.keys(changes).length === 0) {
        setEditing(false);
        helpers.setSubmitting(false);
        return;
      }

      const { rule, ...details } = changes;
      const result = isDynamic
        ? await memberGroupDetails.updateDynamicGroup(changes)
        : await memberGroupDetails.updateGroup(details);

      if (result.ok) {
        notification.enqueueSuccessSnackbar('Changes saved');
        setEditing(false);
      } else {
        const { fieldErrors } = formErrors(result.error);
        if (fieldErrors?.name?.length) {
          helpers.setFieldError('name', fieldErrors.name[0]);
        } else if (fieldErrors?.description?.length) {
          helpers.setFieldError('description', fieldErrors.description[0]);
        } else if (fieldErrors?.rule?.length) {
          notification.enqueueErrorSnackbar(fieldErrors.rule[0]);
        } else {
          notification.enqueueErrorSnackbar(getErrorMessage(result.error, 'Changes could not be saved'));
        }
      }
      helpers.setSubmitting(false);
    };

    return (
      <Section>
        <SectionHeader>
          <SectionTitle>Details</SectionTitle>
        </SectionHeader>
        <SectionBody>
          <Formik initialValues={initialValues} validationSchema={memberGroupValidationSchema} onSubmit={onSubmit}>
            {({ isSubmitting, dirty }) => (
              <Form>
                <NavigationBlocker
                  when={dirty}
                  shouldBlockNavigation={() => dirty}
                  navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
                />
                <MemberGroupForm showRule={isDynamic} ruleGroupUuid={group.uuid} />
                <EditActions>
                  <Button variant="outlined" size="large" type="button" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button variant="contained" size="large" type="submit" disabled={isSubmitting}>
                    Save
                  </Button>
                </EditActions>
              </Form>
            )}
          </Formik>
        </SectionBody>
      </Section>
    );
  }
);

export default EditMemberGroupDetails;
